import SelectInput from '@brands/Components/Inputs/SelectInput/SelectInput';
import { Loading } from '@brands/Components/LoadingSpinner/Loading';
import { config } from '@brands/config/config';
import useCases from '@brands/hooks/useCases';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import { ICase, Status } from '@brands/services/cases/types/ICase';
import { selectAuth } from '@brands/store/selectors/auth';
import { selectDependents } from '@brands/store/selectors/dependents';
import queryString from 'query-string';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { SingleValue } from 'react-select';

import { selectBorderLessCustomStyles } from '../../../Utils/customStyles';
import EditCreditCardModal from '../../Appointments/Patient/EditCreditCardModal/EditCreditCardModal';
import UpcomingServices from '../../Dashboard/Patient/components/UpcomingServices';
import NoCareAvailable from './NoCareAvailable';
import CareJourneyStrip from './CareJourneyStrip';
import { isClosedJourney, useCaseJourneyLookup } from './useCaseJourneyLookup';
import styles from './styles.module.scss';

interface Option {
  value: string;
  label: string;
}

const CareHistory = (): JSX.Element => {
  const location = useLocation();
  const { state } = location;
  const [displayingCases, setDisplayingCases] = useState<ICase[]>([]);
  const [selectedCaseFromUpcomingServices, setSelectedCaseFromUpcomingServices] = useState<ICase>();
  const [editCreditCardModal, setEditCreditCardModal] = useState(false);
  const observer = useRef<IntersectionObserver>();
  const lastMyCasesItemRef = useRef<HTMLTableRowElement>(null);
  const appliedPatientFromQuery = useRef(false);
  const { userInfo } = useAppSelector(selectAuth);
  const { dependents: listOfDependents } = useAppSelector(selectDependents);
  const [patientOptions, setPatientOptions] = useState<Option[]>([]);
  const [patient, setPatient] = useState<Option>(
    state?.dependentId
      ? { value: state.dependentId.toString(), label: 'Dependent' }
      : { value: userInfo.id.toString(), label: 'Myself' },
  );
  const [isLoading, setIsLoading] = useState(false);

  // This screen shows only journeys that are over, complete or canceled.
  // Running and paused journeys surface under Upcoming Appointments instead.
  const caseJourneys = useCaseJourneyLookup([Number(patient.value)], config.features.multiStage);

  const journeyLabel = patient.value !== userInfo.id.toString() ? patient.label : undefined;

  // One strip per journey, on its last visible card — not one per member case,
  // since several cases of a journey can be listed together.
  const journeyAnchors = useMemo(() => {
    const anchors = new Map<number, number>();
    displayingCases.forEach((caseItem) => {
      const match = caseJourneys.byCase.get(Number(caseItem.id));
      if (match && isClosedJourney(match.journey.status))
        anchors.set(match.journey.case_journey_id, Number(caseItem.id));
    });
    return anchors;
  }, [displayingCases, caseJourneys]);

  // Closed journeys whose cases are all filtered out of this list would
  // otherwise vanish from the screen, so they keep the standalone strip.
  const unanchoredJourneys = useMemo(
    () =>
      caseJourneys.journeys.filter(
        (m) => isClosedJourney(m.journey.status) && !journeyAnchors.has(m.journey.case_journey_id),
      ),
    [caseJourneys, journeyAnchors],
  );

  const renderCaseJourneyFooter = (caseItem: ICase): React.ReactNode => {
    const match = caseJourneys.byCase.get(Number(caseItem.id));
    if (!match || journeyAnchors.get(match.journey.case_journey_id) !== Number(caseItem.id)) return null;
    return (
      <CareJourneyStrip
        attached
        patientId={match.patientId}
        journeyId={match.journey.case_journey_id}
        status={match.journey.status}
        patientLabel={journeyLabel}
      />
    );
  };

  const defaultPatient = {
    value: userInfo.id.toString(),
    label: 'Myself',
    status: 'Active',
  };

  useEffect(() => {
    const fetchAndSetPatientOptions = async (): Promise<void> => {
      setIsLoading(true);
      if (userInfo.id) {
        const dependentsWithImages = await Promise.all(
          listOfDependents.map((dependent) => ({
            ...dependent,
            label: dependent?.label ?? '',
            value: dependent?.value ?? '',
          })),
        );
        setPatientOptions([defaultPatient, ...dependentsWithImages]);
      }
      setIsLoading(false);
    };

    fetchAndSetPatientOptions();
  }, [listOfDependents, userInfo.id]);

  // Links that can't carry router state (emails, in-app notifications) name
  // the patient in the query string, as they do for Upcoming appointments, so
  // a guardian lands on the dependent's history rather than their own. Applied
  // once the dependent is among the options (the list can arrive after the
  // first render), and never over a choice made in the dropdown.
  useEffect(() => {
    if (appliedPatientFromQuery.current) return;
    const queryPatientId = queryString.parse(location.search).patientId;
    if (!queryPatientId) return;
    const match = patientOptions.find((option) => option.value === String(queryPatientId));
    if (match) {
      setPatient(match);
      appliedPatientFromQuery.current = true;
    }
  }, [patientOptions, location]);

  // useEffect(() => {
  //   if (state?.dependentId) {
  //     console.log('State dependentId:', state.dependentId);
  //     listOfDependents.forEach((dependent) => {
  //       console.log('Checking dependent:', dependent);
  //       if (Number(dependent?.value) === Number(state.dependentId)) {
  //         setPatient({ value: dependent?.value ?? '', label: dependent?.label ?? '' });
  //       }
  //     });
  //   }
  // }, [state]);

  const params = useMemo(
    () => ({
      sort_by: 'updated_at' as const,
      sort_direction: 'desc' as const,
      patient_id: Number(patient.value),
      include_vc_stats: true,
      status: [Status.Completed, Status.Canceled],
      include_dependents_cases: false,
    }),
    [patient],
  );

  const upcomingAppointmentParams = useMemo(
    () => ({
      sort_by: 'created_at' as const,
      sort_direction: 'desc' as const,
      patient_id: Number(patient.value),
      include_vc_stats: false,
      status: [
        Status.New,
        Status.AcceptedCareAdvisor,
        Status.AcceptedProvider,
        Status.InProgressProvider,
        Status.InProgressCareAdvisor,
        Status.CompletedByProvider,
      ],
      include_dependents_cases: false,
      limit: 1,
    }),
    [patient],
  );

  const { cases, dataLoading, reloadAll, loadNextPage } = useCases(params, true);
  const { cases: upcomingCases, dataLoading: upcomingCasesLoading } = useCases(upcomingAppointmentParams, true);

  useEffect(() => {
    if (cases) setDisplayingCases(cases);
  }, [cases]);

  const loadMore = (): void => {
    setIsLoading(true);
    loadNextPage().finally(() => setIsLoading(false));
  };

  const handleIntersection = (entries: IntersectionObserverEntry[]): void => {
    if (entries[0].isIntersecting && !isLoading) loadMore();
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver(handleIntersection);
      const currentRef = lastMyCasesItemRef.current;
      if (currentRef) observer.current.observe(currentRef);
    }, 100);

    return () => {
      clearTimeout(timer);
      if (observer.current) observer.current.disconnect();
    };
  }, [cases]);

  return (
    <div className={styles.dashboardContainer}>
      {unanchoredJourneys.map((match) => (
        <CareJourneyStrip
          key={match.journey.case_journey_id}
          patientId={match.patientId}
          journeyId={match.journey.case_journey_id}
          status={match.journey.status}
          patientLabel={journeyLabel}
          style={{ marginBottom: 12 }}
        />
      ))}
      <div className={styles.stateContainer}>
        <p className={`${styles.yourStateTitle} fs-unmask`}>Care history for</p>
        <SelectInput
          name="patient"
          options={patientOptions}
          selectorClass={styles.selector}
          style={selectBorderLessCustomStyles}
          containerClass={`${styles.selectorContainer} fs-mask`}
          selectedValue={patientOptions.find((option) => option.value === patient?.value)}
          onChange={(selected: SingleValue<Option>) => {
            appliedPatientFromQuery.current = true;
            if (selected) setPatient(selected);
          }}
        />
      </div>

      {displayingCases.length > 0 ? (
        <UpcomingServices
          displayingCases={displayingCases}
          dataLoading={dataLoading}
          reloadAll={reloadAll}
          setEditCreditCardModal={setEditCreditCardModal}
          selectedCaseFromUpcomingServices={selectedCaseFromUpcomingServices}
          setSelectedCaseFromUpcomingServices={setSelectedCaseFromUpcomingServices}
          lastMyCasesItemRef={lastMyCasesItemRef}
          isLoading={isLoading}
          renderCaseFooter={renderCaseJourneyFooter}
        />
      ) : !dataLoading && !upcomingCasesLoading ? (
        <NoCareAvailable upcomingAppointment={false} hasUpcomingAppointment={upcomingCases.length > 0} />
      ) : (
        <Loading dotted />
      )}

      {editCreditCardModal && (
        <EditCreditCardModal
          setOpenModal={setEditCreditCardModal}
          isOpen={editCreditCardModal}
          thisCase={selectedCaseFromUpcomingServices!}
          reloadAll={reloadAll}
          refreshAll={reloadAll}
        />
      )}
    </div>
  );
};

export default CareHistory;
